import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Search, ArrowUpRight, X, Loader2, LayoutTemplate, Lock, Star } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import TemplateThumbnail from '../shared/TemplateThumbnail';
import UpgradeModal from '../shared/UpgradeModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const USER_KEY = 'ivisora_user';

const getUserPlan = () => {
  try {
    const u = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    return u?.plan || 'free';
  } catch { return 'free'; }
};

const TemplateTile = ({ template, onClick, onUpgrade, userPlan, className = '' }) => {
  const isLocked = template.is_premium && userPlan === 'free';
  return (
    <div
      onClick={isLocked ? onUpgrade : onClick}
      className={`group cursor-pointer rounded-lg overflow-hidden border hover:border-purple-400 transition-all hover:shadow-lg hover:-translate-y-0.5 ${className}`}
    >
      <TemplateThumbnail
        template={template}
        imgClassName="group-hover:scale-[1.03] transition-transform duration-300"
      >
        {template.is_premium && (
          <div className="absolute top-1.5 right-1.5 z-10">
            <span className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 bg-amber-400 text-amber-900 rounded-full shadow">
              <Star className="w-2 h-2 fill-amber-900" /> PRO
            </span>
          </div>
        )}
        {isLocked && (
          <div className="absolute inset-0 bg-black/25 backdrop-blur-[1px] z-[5] flex items-center justify-center">
            <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow">
              <Lock className="w-4 h-4 text-amber-500" />
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-2 left-2 right-2">
            <p className="text-white text-xs font-medium truncate">{template.name}</p>
          </div>
        </div>
      </TemplateThumbnail>
    </div>
  );
};

const TemplatePanel = ({ templateTypeId, onSelectTemplate, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [showFullPopup, setShowFullPopup] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const userPlan = getUserPlan();

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch all themes from admin panel — runs once on mount
  useEffect(() => {
    setThemesLoading(true);
    fetch(`${API_URL}/api/template-themes`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setThemes(Array.isArray(d) ? d : []))
      .catch(() => setThemes([]))
      .finally(() => setThemesLoading(false));
  }, []);

  // Fetch templates filtered by document type + selected theme + search
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '100' });
    if (templateTypeId) params.set('template_type_id', templateTypeId);
    if (selectedTheme !== 'all') params.set('template_category_id', selectedTheme);
    if (debouncedSearch) params.set('search', debouncedSearch);
    // Signed in, the list includes the pages of paid/premium templates this
    // user is entitled to; otherwise those cards come without them.
    const token = localStorage.getItem('ivisora_auth_token') || localStorage.getItem('user_token');
    fetch(`${API_URL}/api/public/templates?${params}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(r => r.ok ? r.json() : [])
      .then(d => setTemplates(Array.isArray(d) ? d : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [templateTypeId, selectedTheme, debouncedSearch]);

  const handleSelectTemplate = (template) => {
    if (onSelectTemplate) onSelectTemplate(template);
    setShowFullPopup(false);
  };

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    setSearchQuery('');
  };

  // ── Theme sidebar (used in full popup) ────────────────────────────────────
  const themeSidebar = (
    <div className="w-56 bg-gray-50 border-r flex-shrink-0 flex flex-col">
      <div className="p-4 border-b flex-shrink-0">
        <h3 className="font-semibold text-gray-900 text-sm">Themes</h3>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2">
          <button
            onClick={() => handleThemeSelect('all')}
            className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-all ${
              selectedTheme === 'all' ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <span className="text-sm font-medium">All Themes</span>
          </button>

          {themesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
            </div>
          ) : themes.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-2">No themes available</p>
          ) : (
            themes.map(theme => (
              <button
                key={theme.id}
                onClick={() => handleThemeSelect(theme.id)}
                className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-all ${
                  selectedTheme === theme.id ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span className="text-sm font-medium">{theme.name}</span>
              </button>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <>
      {/* ── Small sidebar panel ── */}
      <div className="h-full flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Templates</h3>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowFullPopup(true)}>
                <ArrowUpRight className="w-4 h-4" />
              </Button>
              {onClose && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
              <LayoutTemplate className="w-10 h-10 text-gray-200 mb-2" />
              <p className="text-sm text-gray-400">No templates yet</p>
            </div>
          ) : (
            <div className="p-3 grid grid-cols-2 gap-3">
              {templates.slice(0, 8).map(t => (
                <TemplateTile key={t.id} template={t} onClick={() => handleSelectTemplate(t)} userPlan={userPlan} onUpgrade={() => setShowUpgradeModal(true)} />
              ))}
            </div>
          )}
          {templates.length > 8 && (
            <div className="px-3 pb-4">
              <Button variant="outline" className="w-full" onClick={() => setShowFullPopup(true)}>
                View All ({templates.length})
              </Button>
            </div>
          )}
        </ScrollArea>
      </div>

      {/* ── Full popup dialog ── */}
      <Dialog open={showFullPopup} onOpenChange={setShowFullPopup}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden [&>button]:hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-xl font-bold">All Templates</h2>
            <Button variant="ghost" size="icon" onClick={() => setShowFullPopup(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex h-[80vh]">
            {themeSidebar}

            {/* Right — template grid */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="p-4 border-b flex items-center gap-4 flex-shrink-0">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or tag..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Clear
                  </button>
                )}
                <span className="text-sm text-gray-500 flex-shrink-0">{templates.length} templates</span>
              </div>

              {/* Active filter chips */}
              {(selectedTheme !== 'all' || searchQuery) && (
                <div className="px-4 py-2 border-b flex items-center gap-2 flex-wrap">
                  {selectedTheme !== 'all' && (
                    <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-medium">
                      {themes.find(t => t.id === selectedTheme)?.name || 'Theme'}
                      <button onClick={() => handleThemeSelect('all')} className="hover:text-purple-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                      "{searchQuery}"
                      <button onClick={() => setSearchQuery('')} className="hover:text-gray-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}

              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <LayoutTemplate className="w-12 h-12 text-gray-200 mb-3" />
                    <p className="font-medium">No templates found</p>
                    <p className="text-sm text-gray-400 mt-1">Try a different theme or search term</p>
                  </div>
                ) : (
                  <div className="p-4 grid grid-cols-4 gap-4">
                    {templates.map(t => (
                      <TemplateTile key={t.id} template={t} onClick={() => handleSelectTemplate(t)} userPlan={userPlan} onUpgrade={() => { setShowFullPopup(false); setShowUpgradeModal(true); }} />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
    </>
  );
};

export default TemplatePanel;
